import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Calendar,
  CheckCircle,
  CheckSquare,
  Clock,
  Eye,
  Filter,
  Mail,
  MoreVertical,
  Phone,
  Search,
  Send,
  Star,
  User,
  Users,
  X,
  XCircle
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const Applications = () => {
  const { token } = useAuth();
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showBulkRequest, setShowBulkRequest] = useState(false);
  const [bulkRequestData, setBulkRequestData] = useState({
    title: '',
    jobId: '',
    message: ''
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    jobId: '',
    dateRange: '',
    sortBy: '-appliedDate'
  });
  const [jobs, setJobs] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalApplications: 0,
    limit: 12
  });

  // NEW: bulk selection
  const [selectedApps, setSelectedApps] = useState([]);

  useEffect(() => {
    fetchApplications();
    fetchJobs();
  }, [filters, pagination.currentPage]);

  useEffect(() => {
    filterApplications();
  }, [applications, filters]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.limit,
        ...(filters.status && { status: filters.status }),
        ...(filters.jobId && { jobId: filters.jobId })
      });

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/applications?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setApplications(response.data.data.applications || []);
        setPagination(prev => ({
          ...prev,
          totalPages: response.data.data.pagination?.totalPages || 1,
          totalApplications: response.data.data.pagination?.totalApplications || 0
        }));
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/jobs?limit=100&status=active`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        setJobs(response.data.data.jobs || []);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const filterApplications = () => {
    let filtered = [...applications];

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(app => 
        app.user?.firstName?.toLowerCase().includes(searchTerm) ||
        app.user?.lastName?.toLowerCase().includes(searchTerm) ||
        app.user?.email?.toLowerCase().includes(searchTerm) ||
        app.job?.title?.toLowerCase().includes(searchTerm)
      );
    }

    setFilteredApplications(filtered);
  };

  const handleStatusUpdate = async (applicationId, newStatus, notes = '') => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/applications/${applicationId}/status`,
        { status: newStatus, notes },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success(`Application ${newStatus.toLowerCase()} successfully`);
        fetchApplications();
        
        if (selectedApplication && selectedApplication._id === applicationId) {
          setSelectedApplication({ ...selectedApplication, status: newStatus });
        }
      }
    } catch (error) {
      console.error('Error updating application status:', error);
      toast.error(error.response?.data?.message || 'Failed to update application status');
    }
  };

  // Status flow definition
  const statusFlow = ['Application Review', 'Shortlisted', 'Interview Scheduled', 'Interviewed', 'Selected', 'Offered', 'Offer Accepted', 'Hired', 'Onboarded'];

  const getStatusColor = (status, hasAccess = false) => {
    const statusKey = hasAccess && status?.toLowerCase() === 'contact requested' ? 'contact approved' : status?.toLowerCase();
    switch (statusKey) {
      case 'applied': return 'bg-blue-100 text-blue-800';
      case 'under review': return 'bg-yellow-100 text-yellow-800';
      case 'shortlisted': return 'bg-purple-100 text-purple-800';
      case 'interview scheduled':
      case 'interviewed': return 'bg-indigo-100 text-indigo-800';
      case 'offered': return 'bg-teal-100 text-teal-800';
      case 'offer accepted': return 'bg-teal-200 text-teal-900';
      case 'hired': return 'bg-green-200 text-green-900';
      case 'onboarded': return 'bg-green-300 text-green-900';
      case 'selected': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'contact approved': return 'bg-green-100 text-green-800';
      case 'contact requested': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status, hasAccess = false) => {
    const statusKey = hasAccess && status?.toLowerCase() === 'contact requested' ? 'contact approved' : status?.toLowerCase();
    switch (statusKey) {
      case 'applied': return <Clock className="w-4 h-4" />;
      case 'under review': return <Eye className="w-4 h-4" />;
      case 'shortlisted': return <Star className="w-4 h-4" />;
      case 'interview scheduled':
      case 'interviewed': return <Users className="w-4 h-4" />;
      case 'offered': return <Send className="w-4 h-4" />;
      case 'offer accepted': return <CheckCircle className="w-4 h-4" />;
      case 'hired': return <CheckCircle className="w-4 h-4" />;
      case 'onboarded': return <CheckCircle className="w-4 h-4" />;
      case 'selected': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      case 'contact approved': return <CheckCircle className="w-4 h-4" />;
      case 'contact requested': return <Mail className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const calculateSkillMatch = (user, job) => {
    if (!user?.skills?.length || !job?.skills?.length) return 0;
    
    // Handle skills as objects with name property
    const userSkills = user.skills.map(skill => 
      typeof skill === 'string' ? skill.toLowerCase() : skill.name?.toLowerCase()
    ).filter(Boolean);
    
    const jobSkills = job.skills.map(skill => 
      typeof skill === 'string' ? skill.toLowerCase() : skill.name?.toLowerCase()
    ).filter(Boolean);
    
    const matchingSkills = userSkills.filter(skill => 
      jobSkills.some(jobSkill => jobSkill.includes(skill) || skill.includes(jobSkill))
    );
    
    return Math.round((matchingSkills.length / jobSkills.length) * 100);
  };

  const calculateMatchScore = (user, job) => {
    if (!user?.skills || !job?.skills) return 0;
    
    const userSkills = user.skills.map(skill => skill.toLowerCase());
    const jobSkills = job.skills.map(skill => skill.toLowerCase());
    
    const matchingSkills = userSkills.filter(skill => 
      jobSkills.some(jobSkill => jobSkill.includes(skill) || skill.includes(jobSkill))
    );
    
    return Math.round((matchingSkills.length / jobSkills.length) * 100);
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPagination({ ...pagination, currentPage: 1 });
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      jobId: '',
      dateRange: '',
      sortBy: '-appliedDate'
    });
  };

  const handlePageChange = (page) => {
    setPagination({ ...pagination, currentPage: page });
  };

  // Toggle application selection
  const handleApplicationSelect = (appId) => {
    setSelectedApps(prev => {
      if (prev.includes(appId)) {
        return prev.filter(id => id !== appId);
      }
      return [...prev, appId];
    });
  };

  // Bulk request submission
  const handleBulkRequest = async () => {
    if (selectedApps.length === 0) {
      toast.error('Please select at least one applicant');
      return;
    }
    if (!bulkRequestData.title.trim()) {
      toast.error('Please enter a collection name');
      return;
    }
    if (!bulkRequestData.message.trim()) {
      toast.error('Please enter a justification');
      return;
    }

    // Map selected app IDs -> candidate IDs
    const candidateIds = applications
      .filter(app => selectedApps.includes(app._id))
      .map(app => (app.user?._id || app.applicant?._id))
      .filter(Boolean);

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/requests`, {
        candidateIds,
        jobId: bulkRequestData.jobId || null,
        message: bulkRequestData.message,
        title: bulkRequestData.title,
        applicationIds: selectedApps
      }, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      toast.success('Bulk request sent');
      // Refresh list from backend to reflect new statuses
      await fetchApplications();
      setSelectedApps([]);
      setShowBulkRequest(false);
      setBulkRequestData({ title: '', jobId: '', message: '' });
    } catch (error) {
      console.error('Bulk request error:', error);
      toast.error(error.response?.data?.message || 'Failed to send request');
    }
  };

  if (loading && applications.length === 0) {
    return (
      <div className="p-6 max-w-screen-2xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-64 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-screen-2xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Applications</h1>
        <p className="text-gray-600">
          Review and manage job applications from candidates
        </p>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card mb-8"
      >
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by candidate name, email, or job title..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>

          {/* Filter Controls */}
          <div className="flex flex-wrap gap-4">
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input-field min-w-[140px]"
            >
              <option value="">All Status</option>
              <option value="applied">Applied</option>
              <option value="under-review">Under Review</option>
              <option value="shortlisted">Shortlisted</option>
              <option value="interviewed">Interviewed</option>
              <option value="selected">Selected</option>
              <option value="rejected">Rejected</option>
            </select>

            <select
              value={filters.jobId}
              onChange={(e) => handleFilterChange('jobId', e.target.value)}
              className="input-field min-w-[160px]"
            >
              <option value="">All Jobs</option>
              {jobs.map(job => (
                <option key={job._id} value={job._id}>
                  {job.title}
                </option>
              ))}
            </select>

            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="input-field min-w-[140px]"
            >
              <option value="-appliedDate">Newest First</option>
              <option value="appliedDate">Oldest First</option>
              <option value="status">Status</option>
              <option value="-matchScore">Best Match</option>
            </select>
          </div>
        </div>

        {/* Results Summary */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Showing {filteredApplications.length} of {pagination.totalApplications} applications
          </p>
          <div className="flex items-center space-x-4">
            {(filters.search || filters.status || filters.jobId) && (
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear Filters
              </button>
            )}
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">Filtered</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Bulk action bar */}
      {selectedApps.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6"
        >
          <span className="text-sm text-blue-700">{selectedApps.length} selected</span>
          <button onClick={() => setShowBulkRequest(true)} className="btn-primary flex items-center">
            <Send className="w-4 h-4 mr-2" /> Send Request
          </button>
        </motion.div>
      )}

      {/* Applications Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
      >
        {filteredApplications.map((application, index) => (
          <motion.div
            key={application._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.05 }}
            className="card hover:shadow-hover transition-all duration-300"
          >
            {/* Selection Checkbox & Header */}
            <div className="flex items-start justify-between mb-4">
              <button
                onClick={() => handleApplicationSelect(application._id)}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center mr-2 ${
                  selectedApps.includes(application._id)
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'border-gray-300 hover:border-blue-500'}
                `}
              >
                {selectedApps.includes(application._id) && <CheckSquare className="w-3 h-3" />}
              </button>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  {application.user?.profilePicture || application.applicant?.profilePicture ? (
                    <img
                      src={application.user?.profilePicture || application.applicant?.profilePicture}
                      alt="Candidate"
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <span className="text-lg font-medium text-blue-700">
                      {((application.user?.firstName || application.applicant?.firstName || '').charAt(0)).toUpperCase()}{((application.user?.lastName || application.applicant?.lastName || '').charAt(0)).toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {application.hasContactAccess
                      ? `${application.user?.firstName || application.applicant?.firstName || ''} ${application.user?.lastName || application.applicant?.lastName || ''}`.trim()
                      : `${((application.user?.firstName || application.applicant?.firstName || '').charAt(0)).toUpperCase()}${((application.user?.lastName || application.applicant?.lastName || '').charAt(0)).toUpperCase()}`}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {(() => {
                      const email = application.user?.email || application.applicant?.email;
                      if (!email) return 'Email hidden';
                      if (!application.hasContactAccess) {
                        return email.includes('@') ? `${email.substring(0,3)}***@***.***` : 'Email hidden';
                      }
                      return email;
                    })()}
                  </p>
                </div>
              </div>
              
              <div className="relative">
                <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                  <MoreVertical className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Job Info */}
            <div className="mb-4">
              <h4 className="font-medium text-gray-900 mb-1">{application.job?.title}</h4>
              <p className="text-sm text-gray-600">
                {application.job?.location?.city 
                  ? `${application.job.location.city}, ${application.job.location.state}` 
                  : 'Location not specified'
                }
              </p>
            </div>

            {/* Status and Match Score */}
            <div className="flex items-center justify-between mb-4">
              <span className={`status-badge ${getStatusColor(application.status, application.hasContactAccess)} flex items-center`}>
                {getStatusIcon(application.status, application.hasContactAccess)}
                <span className="ml-1 capitalize">{(() => {
                  if (application.hasContactAccess && application.status?.toLowerCase() === 'contact requested') return 'Contact Approved';
                  return application.status?.replace(/-/g, ' ');
                })()}</span>
              </span>
              
              <div className="flex items-center space-x-1">
                <Star className="w-4 h-4 text-yellow-400 fill-current" />
                <span className="text-sm font-medium text-gray-700">
                  {calculateSkillMatch(application.user || application.applicant, application.job)}%
                </span>
              </div>
            </div>

            {/* Application Date */}
            <div className="flex items-center text-sm text-gray-600 mb-4">
              <Calendar className="w-4 h-4 mr-1" />
              Applied {new Date(application.appliedAt || application.appliedDate || application.createdAt).toLocaleDateString()}
            </div>

            {/* Skills Preview */}
            {(application.user?.skills || application.applicant?.skills) && (application.user?.skills || application.applicant?.skills).length > 0 && (
              <div className="mb-4">
                <div className="flex flex-wrap gap-1">
                  {(application.user?.skills || application.applicant?.skills).slice(0, 3).map((skill, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-gray-100 text-gray-700"
                    >
                      {typeof skill === 'string' ? skill : skill.name}
                    </span>
                  ))}
                  {(application.user?.skills || application.applicant?.skills).length > 3 && (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-gray-100 text-gray-700">
                      +{(application.user?.skills || application.applicant?.skills).length - 3} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Indicator when contact request pending */}
            {application.status?.toLowerCase() === 'contact requested' && !application.hasContactAccess && (
              <p className="text-xs text-orange-600 font-medium mb-2">Contact request pending admin approval</p>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              {application.hasContactAccess ? (
                <button
                  onClick={() => {
                    setSelectedApplication(application);
                    setShowDetails(true);
                  }}
                  className="btn-secondary text-sm py-2 px-4 flex items-center"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  View Details
                </button>
              ) : (
                <span className="text-sm text-gray-500 italic">Access restricted</span>
              )}

              {application.hasContactAccess && (
                <div className="flex items-center space-x-2">
                  {application.status?.toLowerCase() === 'applied' && (
                    <button
                      onClick={() => handleStatusUpdate(application._id, 'Under Review')}
                      className="text-blue-600 hover:text-blue-700 p-2 rounded-lg hover:bg-blue-50"
                      title="Move to Review"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  )}
                  
                  {(application.status?.toLowerCase() === 'under review' || application.status?.toLowerCase() === 'applied') && (
                    <button
                      onClick={() => handleStatusUpdate(application._id, 'Shortlisted')}
                      className="text-purple-600 hover:text-purple-700 p-2 rounded-lg hover:bg-purple-50"
                      title="Shortlist"
                    >
                      <Star className="w-4 h-4" />
                    </button>
                  )}
                  
                  {application.status?.toLowerCase() !== 'rejected' && application.status?.toLowerCase() !== 'selected' && (
                    <button
                      onClick={() => handleStatusUpdate(application._id, 'Rejected')}
                      className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50"
                      title="Reject"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Empty State */}
      {filteredApplications.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="empty-state"
        >
          <Users className="empty-state-icon" />
          <h3 className="empty-state-title">
            {filters.search || filters.status || filters.jobId
              ? 'No applications found'
              : 'No applications yet'
            }
          </h3>
          <p className="empty-state-description">
            {filters.search || filters.status || filters.jobId
              ? 'Try adjusting your search criteria or filters'
              : 'Applications will appear here as candidates apply to your jobs'
            }
          </p>
          {(filters.search || filters.status || filters.jobId) && (
            <button onClick={clearFilters} className="btn-secondary">
              Clear Filters
            </button>
          )}
        </motion.div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="pagination"
        >
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1}
            className="pagination-item disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          {[...Array(pagination.totalPages)].map((_, index) => {
            const page = index + 1;
            return (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`pagination-item ${
                  pagination.currentPage === page ? 'active' : ''
                }`}
              >
                {page}
              </button>
            );
          })}
          
          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages}
            className="pagination-item disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </motion.div>
      )}

      {/* Application Details Modal */}
      {showDetails && selectedApplication && (
        <div className="modal-overlay" onClick={() => setShowDetails(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="text-xl font-semibold text-gray-900">
                Application Details
              </h3>
              <button
                onClick={() => setShowDetails(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="modal-body space-y-6">
              {/* Candidate Info */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Candidate Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <User className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">
                        {((selectedApplication.user?.firstName || selectedApplication.applicant?.firstName || '').charAt(0)).toUpperCase()}{((selectedApplication.user?.lastName || selectedApplication.applicant?.lastName || '').charAt(0)).toUpperCase()}
                      </p>
                      <p className="text-sm text-gray-600">Full Name</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">
                        {(() => {
                          const email = selectedApplication.user?.email || selectedApplication.applicant?.email;
                          if (!email) return 'Email hidden';
                          if (!selectedApplication.hasContactAccess) {
                            return email.includes('@') ? `${email.substring(0,3)}***@***.***` : 'Email hidden';
                          }
                          return email;
                        })()}
                      </p>
                      <p className="text-sm text-gray-600">Email</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">
                        {(() => {
                          const phone = selectedApplication.user?.phone || selectedApplication.applicant?.phone;
                          if (!phone) return 'Phone hidden';
                          if (!selectedApplication.hasContactAccess) {
                            return '***-***-****';
                          }
                          return phone;
                        })()}
                      </p>
                      <p className="text-sm text-gray-600">Phone</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Job Info */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Job Information</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">{selectedApplication.job?.title}</h5>
                  <p className="text-sm text-gray-600 mb-2">
                    {selectedApplication.job?.location?.city 
                      ? `${selectedApplication.job.location.city}, ${selectedApplication.job.location.state}` 
                      : 'Location not specified'
                    }
                  </p>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <span>{selectedApplication.job?.employmentType || selectedApplication.job?.jobType || 'Full-time'}</span>
                    <span>{selectedApplication.job?.workMode || 'On-site'}</span>
                  </div>
                </div>
              </div>

              {/* Skills */}
              {(selectedApplication.user?.skills || selectedApplication.applicant?.skills) && (selectedApplication.user?.skills || selectedApplication.applicant?.skills).length > 0 && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {(selectedApplication.user?.skills || selectedApplication.applicant?.skills).map((skill, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                      >
                        {typeof skill === 'string' ? skill : skill.name}
                        {typeof skill === 'object' && skill.level && (
                          <span className="ml-2 text-xs bg-blue-200 px-1 rounded">
                            {skill.level}
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Cover Letter */}
              {selectedApplication.coverLetter && (
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Cover Letter</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700 whitespace-pre-line">{selectedApplication.coverLetter}</p>
                  </div>
                </div>
              )}

              {/* Status Update */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Update Status</h4>
                <div className="flex flex-wrap gap-3">
                  {(() => {
                    const currentIdx = statusFlow.findIndex(s => s.toLowerCase() === (selectedApplication.status || '').toLowerCase());
                    const nextIdx = currentIdx + 1;
                    return statusFlow.map((status, idx) => {
                      const disabled = idx !== nextIdx; // only next status enabled
                      return (
                        <button
                          key={status}
                          onClick={() => {
                            if (disabled) return;
                            handleStatusUpdate(selectedApplication._id, status);
                            setShowDetails(false);
                          }}
                          disabled={disabled}
                          className={`btn-secondary text-sm py-2 px-4 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {status}
                        </button>
                      );
                    });
                  })()}
                  {/* Reject button always available */}
                  <button
                    onClick={() => {
                      handleStatusUpdate(selectedApplication._id, 'Rejected');
                      setShowDetails(false);
                    }}
                    className="text-white bg-red-600 hover:bg-red-700 text-sm py-2 px-4 rounded"
                  >
                    Reject
                  </button>

                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowDetails(false)}
                className="btn-secondary"
              >
                Close
              </button>
              <button className="btn-primary flex items-center">
                <Mail className="w-4 h-4 mr-2" />
                Request Contact
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Bulk Request Modal */}
      {showBulkRequest && (
        <div className="modal-overlay" onClick={() => setShowBulkRequest(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="modal-content" onClick={(e)=>e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-xl font-semibold text-gray-900">Request Contact Details</h3>
              <button onClick={()=>setShowBulkRequest(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="modal-body space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3"><Users className="w-5 h-5 text-blue-600" /><p className="text-sm text-blue-700">Admin will review your request. Selected {selectedApps.length} applicant(s).</p></div>
              </div>
              <div>
                <label className="form-label">Collection Name *</label>
                <input type="text" className="input-field" value={bulkRequestData.title} onChange={e=>setBulkRequestData({...bulkRequestData,title:e.target.value})} />
              </div>
              <div>
                <label className="form-label">Business Justification *</label>
                <textarea rows={4} className="input-field" value={bulkRequestData.message} onChange={e=>setBulkRequestData({...bulkRequestData,message:e.target.value})} />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={()=>setShowBulkRequest(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleBulkRequest} className="btn-primary flex items-center"><Send className="w-4 h-4 mr-2" />Submit</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Applications; 